import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { asArray } from "../lib/api";
import { workCoverUrl } from "../lib/media";
import { useI18n } from "../contexts/I18nContext";
import useReveal from "../hooks/useReveal";
import { ArrowRight, ArrowLeft } from "lucide-react";

export default function WorksPage() {
    const { t, pick, isRTL } = useI18n();
    const Arrow = isRTL ? ArrowLeft : ArrowRight;
    const ref = useReveal();
    const [works, setWorks] = useState([]);
    const [filter, setFilter] = useState("__all__");

    useEffect(() => {
        api.get("/works?limit=200").then((r) => setWorks(asArray(r.data, "works"))).catch(() => {});
    }, []);

    const categories = useMemo(() => {
        const set = new Map();
        works.forEach((w) => {
            const key = pick(w, "category");
            if (key) set.set(key, key);
        });
        return Array.from(set.keys());
    }, [works, pick]);

    const filtered = useMemo(() => {
        if (filter === "__all__") return works;
        return works.filter((w) => pick(w, "category") === filter);
    }, [works, filter, pick]);

    return (
        <div ref={ref} data-testid="works-page">
            <section className="bg-[#EFF0F1] py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    <div className="section-eyebrow mb-5 plus-mark">
                        {t("section_works_overline")}
                    </div>
                    <h1 className="heading page-hero-oneline">
                        {t("section_works_title")}
                    </h1>
                    <div className="mt-12 flex flex-wrap gap-2" data-testid="works-filters">
                        <button
                            onClick={() => setFilter("__all__")}
                            data-testid="works-filter-all"
                            className={`px-5 py-2 text-sm uppercase tracking-[0.2em] border transition-colors ${
                                filter === "__all__" ? "bg-[#002951] text-white border-[#002951]" : "border-black/15 hover:border-[#0060A7] hover:text-[#0060A7]"
                            }`}
                        >
                            {t("works_filter_all")}
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setFilter(c)}
                                data-testid={`works-filter-${c}`}
                                className={`px-5 py-2 text-sm uppercase tracking-[0.2em] border transition-colors ${
                                    filter === c ? "bg-[#002951] text-white border-[#002951]" : "border-black/15 hover:border-[#0060A7] hover:text-[#0060A7]"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white py-12 md:py-16">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filtered.map((w, i) => (
                            <Link
                                key={w.id}
                                to={`/work/${w.slug}`}
                                className="group reveal bg-[#EFF0F1] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                                data-testid={`work-card-${i}`}
                            >
                                <div className="aspect-[16/9] overflow-hidden bg-white">
                                    {workCoverUrl(w) ? (
                                        <img
                                            src={workCoverUrl(w)}
                                            alt={pick(w, "title")}
                                            loading={i < 4 ? "eager" : "lazy"}
                                            fetchPriority={i < 2 ? "high" : "auto"}
                                            decoding="async"
                                            className="h-full w-full object-contain p-2 transition-transform duration-700 group-hover:scale-105"
                                        />
                                    ) : (
                                        <div className="h-full w-full bg-[#002951]" />
                                    )}
                                </div>
                                <div className="p-6 md:p-8">
                                    <div className="text-xs uppercase tracking-[0.25em] text-[#0060A7] mb-3">
                                        {pick(w, "category")} {w.year ? `� ${w.year}` : ""}
                                    </div>
                                    <h3 className="heading text-lg md:text-xl tracking-tight leading-tight font-bold text-[#002951]">
                                        {pick(w, "title")}
                                    </h3>
                                    <div className="mt-4 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[#002951] transition-colors group-hover:text-[#0060A7]">
                                        {t("cta_view_case")} <Arrow size={14} />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
